/* import __COLOCATED_TEMPLATE__ from './ellipsis-menu.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import { objectTypes } from 'embercom/models/data/matching-system/matching-constants';
import { FIN_TASKS_ROUTES } from 'embercom/models/outbound/content-wrapper';

interface Signature {
  Args: {
    config?: {
      duplicateModalComponent?: any;
      hideDuplicateOption?: boolean;
      hideTagOption?: boolean;
      hideDeleteOption?: boolean;
    };
    isBackgroundWorkflow?: boolean;
    isViewMode?: boolean;
  };
}

export default class EllipsisMenu extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare contentEditorService: any;
  @service declare appService: any;
  @service declare notificationsService: any;
  @service declare router: any;
  @service declare workflowLocalizationService: any;
  @service declare workflowEditorService: any;
  @service declare intercomEventService: $TSFixMe;
  @tracked showDuplicateModal = false;
  @tracked showTagModal = false;

  get hideDuplicateOption() {
    return (
      Boolean(this.args.config?.hideDuplicateOption) ||
      this.hideDuplicateOptionForTaskWorkflowWithProcedureRestrictions
    );
  }

  get hideDuplicateOptionForTaskWorkflowWithProcedureRestrictions() {
    let app = this.appService.app;

    return Boolean(
      this.isTaskWorkflow && app?.canUseFinProcedures && !app?.canUseFinProceduresAllowTaskCreation,
    );
  }

  get isTaskWorkflow() {
    let routeName = this.router.currentRouteName;

    return Boolean(
      routeName === FIN_TASKS_ROUTES.task ||
        routeName?.includes('apps.app.standalone.tasks.workflow'),
    );
  }

  get hasDuplicateModalComponent() {
    let { config } = this.args;
    return isPresent(config) && isPresent(config?.duplicateModalComponent);
  }

  get pauseLabel() {
    return this.intl.t(
      'outbound.content-editor.headers.view-mode.standard-header.messages.pause-label',
    );
  }

  get shouldShowVersionHistory() {
    let activeObject = this.contentEditorService.activeObject;
    let workflow = activeObject?.activeWorkflow;
    return Boolean(
      workflow?.id && workflow?.workflowInstanceEntityId && activeObject?.canUseDraftWorkflow?.(),
    );
  }

  @action
  onClickVersionHistory(dropdown: any) {
    dropdown.hideDropdownOverlay();
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'version_history',
    });
    this.workflowEditorService.showVersionHistory = true;
  }

  @action
  async onClickDuplicate(dropdown?: any) {
    let activeRulesetLink = this.contentEditorService.activeRulesetLink;
    if (!activeRulesetLink) {
      return;
    }

    let { objectType } = activeRulesetLink;
    let { hasTests } = this.contentEditorService.ruleset;

    try {
      if (this.appService.app.isStandaloneApp) {
        await this.contentEditorService.duplicateRuleset.perform();
        dropdown?.hideDropdownOverlay();
      } else if (objectType === objectTypes.chat || objectType === objectTypes.post) {
        this.contentEditorService.showDuplicateModal();
      } else if (objectType === objectTypes.customBot && hasTests) {
        await this.contentEditorService.duplicateRuleset.perform();
      } else if (this.hasDuplicateModalComponent) {
        this.showDuplicateModal = true;
      } else {
        await this.contentEditorService.duplicateRuleset.perform();
      }
    } catch (error) {
      console.error('Failed to duplicate ruleset:', error);
      this.notificationsService.notifyError(
        this.intl.t('outbound.content-editor.headers.more-dropdown.duplicate-failed'),
      );
    }
  }
  @action
  async afterTagSave() {
    this.notificationsService.notifyConfirmation(
      this.intl.t('outbound.content-editor.headers.more-dropdown.message-tagged'),
    );
    try {
      await this.contentEditorService.ruleset.reload();
    } catch (error) {
      console.error('Failed to reload ruleset after tagging:', error);
    }
    this.showTagModal = false;
  }
  @action async onClickDelete() {
    try {
      if (this.contentEditorService.returnPath) {
        this.contentEditorService.deleteRuleset.perform({ transitionRoute: true });
      } else if (this.router.currentRouteName === FIN_TASKS_ROUTES.task) {
        await this.contentEditorService.deleteRuleset.perform({ transitionRoute: false });
        this.router.transitionTo(FIN_TASKS_ROUTES.tasks);
      } else if (this.router.currentRouteName?.includes('apps.app.standalone.tasks.workflow')) {
        await this.contentEditorService.deleteRuleset.perform({ transitionRoute: false });
        this.router.transitionTo('apps.app.standalone.tasks.index');
      } else {
        await this.contentEditorService.deleteRuleset.perform();
      }
    } catch (error) {
      console.error('Failed to delete ruleset:', error);
      this.notificationsService.notifyError(
        this.intl.t('outbound.content-editor.headers.more-dropdown.delete-failed'),
      );
    }
  }

  @action
  async onClickPause() {
    try {
      await this.contentEditorService.deactivateRuleset.perform();
    } catch (error) {
      console.error('Failed to pause ruleset:', error);
      this.notificationsService.notifyError(
        this.intl.t('outbound.content-editor.headers.more-dropdown.pause-failed'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'content-editor/headers/ellipsis-menu': typeof EllipsisMenu;
    'ContentEditor::Headers::EllipsisMenu': typeof EllipsisMenu;
    'ContentEditor::Headers::MoreDropdown': any;
    'Common::TaggingComponent': any;
  }
}
